import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeIn, ZoomIn } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { useThemePalette } from '@/config/theme';
import { useChallenges } from '@/features/fantasy/challenges';
import { useAuth } from '@/lib/auth/AuthProvider';
import { dayKey } from '@/lib/i18n/format';

const SHOWN_KEY = 'tashkeela.challengesPopupShown';

/**
 * On entering Fantasy, nudge the user (once per day) to play today's challenges -
 * but only when there are open ones. Renders nothing but the modal.
 */
export function ChallengesPopupGate() {
  const { userId } = useAuth();
  const { t } = useTranslation();
  const palette = useThemePalette();
  const router = useRouter();
  const challenges = useChallenges();
  const [visible, setVisible] = useState(false);
  const decided = useRef(false);

  const openCount = (challenges.data ?? []).length;

  useEffect(() => {
    if (decided.current) return;
    if (!userId || openCount === 0) return;
    decided.current = true;
    const today = dayKey(new Date());
    AsyncStorage.getItem(SHOWN_KEY)
      .then((last) => {
        if (last !== today) setVisible(true);
      })
      .catch(() => setVisible(true));
  }, [userId, openCount]);

  const dismiss = () => {
    AsyncStorage.setItem(SHOWN_KEY, dayKey(new Date())).catch(() => {});
    setVisible(false);
  };

  const play = () => {
    dismiss();
    router.push('/fantasy/challenges' as never);
  };

  if (!visible) return null;
  return (
    <Modal visible transparent animationType="fade" onRequestClose={dismiss} statusBarTranslucent>
      <Animated.View
        entering={FadeIn.duration(180)}
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: 'rgba(0,0,0,0.66)' }}
      >
        <Pressable className="absolute inset-0" accessibilityRole="button" onPress={dismiss} />
        <Animated.View
          entering={ZoomIn.springify().damping(16)}
          className="w-full items-center rounded-3xl border border-volt-line bg-surface-2 px-5 pb-5 pt-6"
          style={{ maxWidth: 360 }}
        >
          <View className="h-16 w-16 items-center justify-center rounded-full bg-volt-glass-strong">
            <Ionicons name="flash" size={30} color={palette.voltBright} />
          </View>
          <AppText variant="title" weight="bold" className="mt-3 text-center text-ink">
            {t('fantasy.challengesPopup.title')}
          </AppText>
          <AppText variant="caption" className="mt-2 text-center text-soft">
            {t('fantasy.challengesPopup.body')}
          </AppText>
          <View className="mt-5 w-full">
            <Button label={t('fantasy.challengesPopup.cta')} variant="primary" onPress={play} />
          </View>
          <Pressable accessibilityRole="button" onPress={dismiss} className="mt-2 items-center py-2">
            <AppText variant="micro" className="text-muted">
              {t('fantasy.challengesPopup.later')}
            </AppText>
          </Pressable>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
